import { all, fork, put, takeLatest, select, call } from "redux-saga/effects";
import { getQuery, postQuery, putQuery } from "utils/api";
import { Toast } from "utils/toast";
import { actions } from "./action";
import { actions as loaderAction } from "redux/Loader/action";
import { utils, writeFile } from "xlsx";

export function* getWorkExperience() {
  yield takeLatest(
    actions.GET_WORK_EXPERIENCE_REQUEST,
    function* ({ data, navigate }) {
      try {
        const response = yield postQuery(`get-work-experience-by-admin`, data);
        if (!response.data.error) {
          yield put({
            type: actions.GET_WORK_EXPERIENCE_SUCCESS,
            payload: response.data.result,
          });
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });
        }
      } catch (e) {
        yield put({
          type: actions.CATCH_ERROR,
          payload: { scope: "general" },
        });
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message);
      }
    }
  );
}

export function* createWorkExperience() {
  yield takeLatest(
    actions.CREATE_WORK_EXPERIENCE_REQUEST,
    function* ({ data }) {
      try {
        const response = yield postQuery(`create/work-experience`, data);
        if (!response.data.error) {
          yield put({
            type: actions.CREATE_WORK_EXPERIENCE_SUCCESS,
            payload: response.data.result,
          });
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });
        }
      } catch (e) {
        yield put({
          type: actions.CATCH_ERROR,
          payload: { scope: "general" },
        });
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message);
      }
    }
  );
}

export function* updateWorkExperience() {
  yield takeLatest(
    actions.UPDATE_WORK_EXPERIENCE_REQUEST,
    function* ({ data }) {
      try {
        const response = yield putQuery(`update/work-experience`, data);
        if (!response.data.error) {
          yield put({
            type: actions.UPDATE_WORK_EXPERIENCE_SUCCESS,
            payload: response.data.result,
          });
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });
        }
      } catch (e) {
        yield put({
          type: actions.CATCH_ERROR,
          payload: { scope: "general" },
        });
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message);
      }
    }
  );
}

export function* getWorkExperienceTiles() {
  yield takeLatest(
    actions.GET_WORK_EXPERIENCE_TILES_REQUEST,
    function* ({ data }) {
      try {
        const response = yield getQuery(
          `get-work-experience-tiles?clubId=${data.clubId}`
        );
        if (!response.data.error) {
          yield put({
            type: actions.GET_WORK_EXPERIENCE_TILES_SUCCESS,
            payload: response.data.result,
          });
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });
        }
      } catch (e) {
        yield put({
          type: actions.CATCH_ERROR,
          payload: { scope: "general" },
        });
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message);
      }
    }
  );
}

// Get Organization Work Experience with progress tracking
export function* getOrgWorkExperience() {
  yield takeLatest(
    actions.GET_ORG_WORK_EXPERIENCE_REQUEST,
    function* ({ clubId }) {
      try {
        yield put({
          type: loaderAction.START_LOADER,
        });

        yield put({
          type: actions.SET_ORG_WORK_EXPERIENCE_LOADING,
          payload: true,
        });

        // Initialize progress
        yield put({
          type: actions.SET_LOADING_PROGRESS,
          payload: { current: 0, total: 1, currentUser: 'Processing all users...' },
        });

        if (!clubId) {
          Toast("error", "No club/organisation ID provided");
          return;
        }

        // Single API call to get all work experience data
        const response = yield postQuery('get-bulk-work-experience-by-club', {
          clubId: clubId
        });
        
        if (response.data.error || !response.data.result) {
          const errorMessage = response.data.error || 'Failed to load work experience data';
          yield put({
            type: actions.CATCH_ERROR,
            payload: { scope: "org" },
          });
          Toast("error", errorMessage);
          return;
        }

        const { users, summary } = response.data.result;

        // Update progress to show completion
        yield put({
          type: actions.SET_LOADING_PROGRESS,
          payload: { 
            current: 1, 
            total: 1, 
            currentUser: `Completed! Processed ${users.length} users` 
          },
        });

        // Update Redux state - same structure as before
        yield put({
          type: actions.GET_ORG_WORK_EXPERIENCE_SUCCESS,
          payload: users,
          summary: summary
        });
        
      } catch (e) {
        if (!e._isAuthError) {
          const errorMessage = e.response?.data?.message || e.response?.data?.error || e.message || 'Unknown error';
          Toast("error", errorMessage);
        }
        
        yield put({
          type: actions.CATCH_ERROR,
          payload: { scope: "org" },
        });
      } finally {
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        yield put({
          type: actions.SET_ORG_WORK_EXPERIENCE_LOADING,
          payload: false,
        });
        // Clear progress when done
        yield put({
          type: actions.SET_LOADING_PROGRESS,
          payload: null,
        });
      }
    }
  );
}

// Excel Download saga - Creates structured Excel file
export function* downloadWorkExperienceCSV() {
  yield takeLatest(
    actions.DOWNLOAD_WORK_EXPERIENCE_CSV,
    function* () {
      try {
        const state = yield select();
        const orgWorkExperiences = state.workExperience.orgWorkExperiences;
        const summary = state.workExperience.orgWorkExperienceSummary;
        
        if (!orgWorkExperiences || orgWorkExperiences.length === 0) {
          Toast("warning", "No work experience data available. Please fetch data first.");
          return;
        }
        
        // Import XLSX utilities directly
        const XLSX = { utils, writeFile };
        
        // Create new workbook
        const workbook = XLSX.utils.book_new();
        
        // SHEET 1: SUMMARY
        const summaryData = [
          ['WORK EXPERIENCE SUMMARY REPORT'],
          ['Generated on:', new Date().toLocaleDateString()],
          [''],
          ['OVERALL STATISTICS'],
          ['Total Users:', summary?.totalUsers || 0],
          ['Users with Work Experience:', summary?.usersWithWorkExperience || 0],
          ['Users without Work Experience:', (summary?.totalUsers || 0) - (summary?.usersWithWorkExperience || 0)],
          ['Total Log Entries:', summary?.totalLogEntries || 0],
          [''],
          ['USER SUMMARY'],
          ['Name', 'Email', 'Hours Completed', 'Hours Required', 'Completion %', 'Log Entries', 'Courses Enrolled']
        ];
        
        // Add each user's summary
        orgWorkExperiences.forEach(user => {
          summaryData.push([
            user.name,
            user.email,
            user.totalHoursCompleted,
            user.totalHoursRequired,
            user.completionPercentage + '%',
            user.logCount,
            user.courses.length
          ]);
        });
        
        // Add individual user log sections
        summaryData.push(['']);
        summaryData.push(['INDIVIDUAL USER WORK EXPERIENCE LOGS']);
        summaryData.push(['']);
        
        orgWorkExperiences.forEach(user => {
          // Add user header
          summaryData.push([`--- ${user.name.toUpperCase()} ---`]);
          summaryData.push(['Email:', user.email]);
          summaryData.push(['Total Hours Completed:', user.totalHoursCompleted]);
          summaryData.push(['Total Hours Required:', user.totalHoursRequired]);
          summaryData.push(['Completion Percentage:', user.completionPercentage + '%']);
          summaryData.push(['Number of Log Entries:', user.logCount]);
          summaryData.push(['']);
          
          if (user.logs && user.logs.length > 0) {
            // Add headers for logs
            summaryData.push(['Date', 'Hours', 'Course', 'Comment/Description']);
            
            // Add each log entry
            user.logs.forEach(log => {
              const course = user.courses.find(c => c.id === log.courseId);
              const courseName = course ? course.name : (log.courseId ? `Course ID: ${log.courseId}` : 'No course specified');
              const logDate = log.createdAt ? new Date(log.createdAt).toLocaleDateString() : 'No date';
              
              summaryData.push([
                logDate,
                log.hours || 0,
                courseName,
                log.comment || 'No comment'
              ]);
            });
          } else {
            summaryData.push(['No work experience logs recorded']);
          }
          
          summaryData.push(['']); // Empty row between users
        });
        
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
        
        // SHEET 2: DETAILED LOGS
        const detailData = [
          ['DETAILED WORK EXPERIENCE LOGS'],
          [''],
          ['User Name', 'Email', 'User ID', 'Course Name', 'Date', 'Hours', 'Comment/Description', 'Course ID', 'Log ID']
        ];
        
        // Add logs for each user
        orgWorkExperiences.forEach(user => {
          if (user.logs && user.logs.length > 0) {
            // Add a separator row for each user
            detailData.push(['']);
            detailData.push([`--- ${user.name.toUpperCase()} ---`]);
            
            user.logs.forEach(log => {
              // Find course name from courses array
              const course = user.courses.find(c => c.id === log.courseId);
              const courseName = course ? course.name : (log.courseId ? `Course ID: ${log.courseId}` : 'No course specified');
              
              detailData.push([
                user.name,
                user.email,
                user.userId,
                courseName,
                log.createdAt ? new Date(log.createdAt).toLocaleDateString() : '',
                log.hours || 0,
                log.comment || '',
                log.courseId || '',
                log.id || ''
              ]);
            });
          } else {
            // User with no logs
            detailData.push(['']);
            detailData.push([`--- ${user.name.toUpperCase()} (NO WORK EXPERIENCE) ---`]);
            detailData.push([
              user.name,
              user.email,
              user.userId,
              'No work experience recorded',
              '',
              0,
              '',
              '',
              ''
            ]);
          }
        });
        
        const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
        XLSX.utils.book_append_sheet(workbook, detailSheet, 'Detailed Logs');
        
        // SHEET 3: COURSES PER USER
        const coursesData = [
          ['USER COURSE ENROLLMENTS'],
          [''],
          ['User Name', 'Course Name', 'Required Hours', 'Course ID']
        ];
        
        orgWorkExperiences.forEach(user => {
          if (user.courses && user.courses.length > 0) {
            // Add separator for each user
            coursesData.push(['']);
            coursesData.push([`--- ${user.name.toUpperCase()} ---`]);
            
            user.courses.forEach(course => {
              if (course.id !== 'all') { // Skip the "All" option
                coursesData.push([
                  user.name,
                  course.name,
                  course.hours || 0,
                  course.id
                ]);
              }
            });
          }
        });
        
        const coursesSheet = XLSX.utils.aoa_to_sheet(coursesData);
        XLSX.utils.book_append_sheet(workbook, coursesSheet, 'User Courses');
        
        // Generate filename and download
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `work_experience_report_${currentDate}.xlsx`;
        
        writeFile(workbook, filename);
        Toast("success", `Excel report downloaded: ${filename}`);
        
      } catch (error) {
        Toast("error", "Failed to create Excel file: " + error.message);
      }
    }
  );
}

export default function* workExperienceSaga() {
  yield all([fork(getWorkExperience)]);
  yield all([fork(createWorkExperience)]);
  yield all([fork(updateWorkExperience)]);
  yield all([fork(getWorkExperienceTiles)]);
  yield all([fork(getOrgWorkExperience)]);
  yield all([fork(downloadWorkExperienceCSV)]);
}